import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { AbstractAppConfig } from '../../../app.config';
import { HttpErrorService } from '../http';
import { CaseEventData, Draft, DRAFT_PREFIX, CaseView } from '../../domain';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/operators';

@Injectable()
export class DraftService {

  public static readonly V2_MEDIATYPE_DRAFT_CREATE =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.ui-draft-create.v2+json;charset=UTF-8';
  public static readonly V2_MEDIATYPE_DRAFT_UPDATE =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.ui-draft-update.v2+json;charset=UTF-8';
  public static readonly V2_MEDIATYPE_DRAFT_READ =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.ui-draft-read.v2+json;charset=UTF-8';
  public static readonly V2_MEDIATYPE_DRAFT_DELETE =
    'application/vnd.uk.gov.hmcts.ccd-data-store-api.ui-draft-delete.v2+json;charset=UTF-8';

  public static readonly QC_V2_MEDIATYPE_DRAFT_CREATE =
    'application/vnd.app.quickcase.store.data.api.ui-draft-create.v2+json;charset=UTF-8';
  public static readonly QC_V2_MEDIATYPE_DRAFT_UPDATE =
    'application/vnd.app.quickcase.store.data.api.ui-draft-update.v2+json;charset=UTF-8';
  public static readonly QC_V2_MEDIATYPE_DRAFT_READ =
    'application/vnd.app.quickcase.store.data.api.ui-draft-read.v2+json;charset=UTF-8';
  public static readonly QC_V2_MEDIATYPE_DRAFT_DELETE =
    'application/vnd.app.quickcase.store.data.api.ui-draft-delete.v2+json;charset=UTF-8';

  constructor(
    private httpClient: HttpClient,
    private appConfig: AbstractAppConfig,
    private errorService: HttpErrorService
  ) {}

  createDraft(ctid: string, eventData: CaseEventData): Observable<Draft> {
    const saveDraftEndpoint = this.appConfig.getCreateOrUpdateDraftsUrl(ctid);
    let headers = new HttpHeaders({
      'Accept': [DraftService.V2_MEDIATYPE_DRAFT_CREATE, DraftService.QC_V2_MEDIATYPE_DRAFT_CREATE],
    });
    return this.httpClient
      .post<Draft>(saveDraftEndpoint, eventData, {headers})
      .pipe(
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  updateDraft(ctid: string, draftId: string, eventData: CaseEventData): Observable<Draft> {
    const saveDraftEndpoint = this.appConfig.getCreateOrUpdateDraftsUrl(ctid) + draftId;
    let headers = new HttpHeaders({
      'Accept': [DraftService.V2_MEDIATYPE_DRAFT_UPDATE, DraftService.QC_V2_MEDIATYPE_DRAFT_UPDATE],
    });
    return this.httpClient
      .put<Draft>(saveDraftEndpoint, eventData, {headers})
      .pipe(
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  getDraft(draftId: string): Observable<CaseView> {
    const url = this.appConfig.getViewOrDeleteDraftsUrl(draftId.slice(DRAFT_PREFIX.length));
    let headers = new HttpHeaders({
      'Accept': [DraftService.V2_MEDIATYPE_DRAFT_READ, DraftService.QC_V2_MEDIATYPE_DRAFT_READ],
    });
    return this.httpClient
      .get<CaseView>(url, {headers})
      .pipe(
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  deleteDraft(draftId: string): Observable<{}> {
    const url = this.appConfig.getViewOrDeleteDraftsUrl(draftId.slice(DRAFT_PREFIX.length));
    let headers = new HttpHeaders({
      'Accept': [DraftService.V2_MEDIATYPE_DRAFT_DELETE, DraftService.QC_V2_MEDIATYPE_DRAFT_DELETE],
    });
    return this.httpClient
      .delete(url, {headers})
      .pipe(
        catchError(error => {
          this.errorService.setError(error);
          return throwError(error);
        })
      );
  }

  createOrUpdateDraft(caseTypeId: string, draftId: string, caseEventData: CaseEventData): Observable<Draft> {
    if (!draftId) {
      return this.createDraft(caseTypeId, caseEventData);
    } else {
      return this.updateDraft(caseTypeId, Draft.stripDraftId(draftId), caseEventData);
    }
  }
}
